import React, { useEffect, useMemo, useState } from 'react';
import { courseService } from '../../services/courseService';
import { Course } from '../../types';
import { schoolSlideService, SchoolSlide, SchoolTrackKey, SchoolSlidePayload } from '../../services/schoolSlideService';
import { SCHOOL_TRACKS } from '../../utils/schoolTracks';
import { getMediaUrl } from '../../utils/media';

const AdminSchoolSlidesPage: React.FC = () => {
  const [track, setTrack] = useState<SchoolTrackKey>('short');
  const [slides, setSlides] = useState<SchoolSlide[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSlide, setEditingSlide] = useState<SchoolSlide | null>(null);
  const [form, setForm] = useState<SchoolSlidePayload>({
    title: '',
    description: '',
    sort_order: 0,
    is_active: true,
    course_id: undefined,
    videoFile: null,
    thumbnailFile: null,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');


  const currentTrackMeta = useMemo(
    () => SCHOOL_TRACKS.find((t) => t.key === track),
    [track]
  );

  const loadSlides = async (selectedTrack: SchoolTrackKey) => {
    setLoading(true);
    try {
      const data = await schoolSlideService.listAdmin(selectedTrack);
      setSlides(data);
    } catch (err: any) {
      console.error('Error loading slides', err);
      setError(err.response?.data?.error || 'Không thể tải danh sách slide');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await courseService.getCourses(1, 100, undefined, 'published');
        setCourses(data.courses);
      } catch (err) {
        console.error('Error loading courses', err);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    loadSlides(track);
  }, [track]);

  const resetForm = () => {
    setEditingSlide(null);
    // Tính sort_order mặc định: số lớn nhất + 1
    const maxSortOrder = slides.length > 0
      ? Math.max(...slides.map(s => s.sort_order || 0))
      : 0;
    setForm({
      title: '',
      description: '',
      sort_order: maxSortOrder + 1,
      is_active: true,
      course_id: undefined,
      videoFile: null,
      thumbnailFile: null,
    });
    setError('');
  };

  const handleEdit = (slide: SchoolSlide) => {
    setEditingSlide(slide);
    setForm({
      title: slide.title,
      description: slide.description || '',
      sort_order: slide.sort_order,
      is_active: slide.is_active,
      course_id: slide.course_id,
      videoFile: null,
      thumbnailFile: null,
    });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!form.title || !form.title.trim()) {
      setError('Tiêu đề là bắt buộc');
      return;
    }
    // Kiểm tra sort_order không trùng (trừ khi đang edit cùng slide)
    const duplicateSlide = slides.find(
      (s) => s.sort_order === form.sort_order && s.id !== editingSlide?.id
    );
    if (duplicateSlide) {
      setError(`Thứ tự hiển thị ${form.sort_order} đã được sử dụng bởi slide "${duplicateSlide.title}". Vui lòng chọn số khác.`);
      return;
    }
    setSaving(true);
    try {
      const payload: SchoolSlidePayload = {
        track,
        title: form.title?.trim() || '',
        description: form.description || '',
        sort_order: form.sort_order ?? 0,
        is_active: form.is_active ?? true,
        course_id: form.course_id || undefined,
        videoFile: form.videoFile || undefined,
        thumbnailFile: form.thumbnailFile || undefined,
      };
      if (editingSlide) {
        await schoolSlideService.update(editingSlide.id, payload);
        setSuccess('Cập nhật slide thành công');
      } else {
        await schoolSlideService.create(payload);
        setSuccess('Tạo slide mới thành công');
      }
      await loadSlides(track);
      resetForm();
      setTimeout(() => setSuccess(''), 2500);
    } catch (err: any) {
      console.error('Error saving slide', err);
      setError(err.response?.data?.error || 'Không thể lưu slide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slide: SchoolSlide) => {
    if (!window.confirm(`Xoá slide "${slide.title}"?`)) return;
    try {
      await schoolSlideService.remove(slide.id);
      setSlides((prev) => prev.filter((s) => s.id !== slide.id));
    } catch (err: any) {
      console.error('Error deleting slide', err);
      setError(err.response?.data?.error || 'Không thể xoá slide');
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Quản lý slide Trường phái video</h1>

      <div className="mb-4 flex gap-2">
        {SCHOOL_TRACKS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setTrack(t.key);
              setSuccess('');
              setError('');
              resetForm();
            }}
            className={`px-4 py-2 rounded-full text-sm font-medium ${t.key === track ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {currentTrackMeta && (
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">{currentTrackMeta.description}</p>
          {(() => {
            const currentCourseId = slides.find(s => s.course_id)?.course_id;
            const currentCourse = currentCourseId ? courses.find(c => c.id === currentCourseId) : null;
            return currentCourse ? (
              <div className="text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-blue-800">
                <strong>Khóa học hiện tại của trường phái này:</strong> {currentCourse.title}
                {currentCourse.price > 0 && (
                  <span className="ml-2 text-blue-600">
                    ({currentCourse.price.toLocaleString('vi-VN')} VNĐ)
                  </span>
                )}
                {currentCourse.price <= 0 && (
                  <span className="ml-2 text-green-600">(Miễn phí)</span>
                )}
              </div>
            ) : (
              <div className="text-sm bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-yellow-800">
                <strong>Lưu ý:</strong> Trường phái này chưa được gắn với khóa học nào.
                Vui lòng chọn khóa học cho một slide bất kỳ trong trường phái này.
              </div>
            );
          })()}
        </div>
      )}

      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 p-3 rounded bg-green-50 text-green-700 border border-green-200 text-sm">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-4 mb-6 space-y-3">
        <h2 className="font-semibold text-gray-900">
          {editingSlide ? 'Chỉnh sửa slide' : 'Tạo slide mới'}
        </h2>

        <div>
          <label className="block text-sm font-medium mb-1">Tiêu đề</label>
          <input
            type="text"
            value={form.title || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Mô tả (có thể xuống dòng bằng Enter)</label>
          <textarea
            value={form.description || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y"
            rows={6}
            placeholder="Nhập mô tả chi tiết. Bạn có thể xuống dòng bằng phím Enter."
            style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Video (tải từ máy)</label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  videoFile: e.target.files && e.target.files[0] ? e.target.files[0] : null,
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            />
            {editingSlide?.video_url && !form.videoFile && (
              <p className="mt-1 text-xs text-gray-500">Video hiện tại: {editingSlide.video_url}</p>
            )}
            {form.videoFile && (
              <p className="mt-1 text-xs text-green-600">File mới đã chọn: {form.videoFile.name}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Thumbnail (tải từ máy)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  thumbnailFile: e.target.files && e.target.files[0] ? e.target.files[0] : null,
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            />
            {editingSlide?.thumbnail_url && !form.thumbnailFile && (
              <p className="mt-1 text-xs text-gray-500">Thumbnail hiện tại: {editingSlide.thumbnail_url}</p>
            )}
            {form.thumbnailFile && (
              <p className="mt-1 text-xs text-green-600">File mới đã chọn: {form.thumbnailFile.name}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Thứ tự hiển thị</label>
            <input
              type="number"
              value={form.sort_order ?? 0}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, sort_order: Number(e.target.value) || 0 }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Khoá học liên kết</label>
            <select
              value={form.course_id ?? ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  course_id: e.target.value ? Number(e.target.value) : undefined,
                }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">Không chọn</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 mt-5 md:mt-0">
            <input
              id="is_active"
              type="checkbox"
              checked={form.is_active ?? true}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
              Kích hoạt slide
            </label>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-60"
          >
            {saving ? 'Đang lưu...' : editingSlide ? 'Lưu thay đổi' : 'Tạo slide'}
          </button>
          {editingSlide && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold"
            >
              Huỷ chỉnh sửa
            </button>
          )}
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h2 className="font-semibold mb-3">Danh sách slide ({slides.length})</h2>
        {loading ? (
          <div className="text-sm text-gray-500">Đang tải...</div>
        ) : slides.length === 0 ? (
          <div className="text-sm text-gray-500">Chưa có slide nào cho trường phái này.</div>
        ) : (
          <div className="space-y-2">
            {slides.map((slide) => (
              <div
                key={slide.id}
                className="flex items-center justify-between gap-4 border border-gray-100 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <div className="w-14 h-10 rounded bg-gray-100 overflow-hidden flex items-center justify-center">
                    {slide.thumbnail_url ? (
                      // eslint-disable-next-line jsx-a11y/alt-text
                      <img
                        src={getMediaUrl(slide.thumbnail_url)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-gray-500">No thumb</span>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{slide.title}</div>
                    <div className="text-xs text-gray-500">
                      Thứ tự: {slide.sort_order} •{' '}
                      {slide.is_active ? 'Đang hiển thị' : 'Đang ẩn'}
                      {slide.course_id && (
                        <>
                          {' • '}
                          Khoá: {courses.find((c) => c.id === slide.course_id)?.title || slide.course_id}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(slide)}
                    className="px-3 py-1 text-xs rounded bg-gray-100 text-gray-700"
                  >
                    Sửa
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(slide)}
                    className="px-3 py-1 text-xs rounded bg-red-100 text-red-700"
                  >
                    Xoá
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSchoolSlidesPage;


